/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react'
import axios from 'axios'
import * as ethers from 'ethers'
import * as zkSyncWeb3 from 'zksync-web3'
import { PaymasterParams } from 'zksync-web3/build/src/types'
import { TENANT_CONTRACTS } from '@config'
import {
  MAINNET_ETH_PAYMASTER_ADDRESS,
  MAINNET_USDC_TOKEN_ADDRESS,
  PRICE_ETH_ID_ZKSYNC_MAINNET,
  PRICE_ETH_ID_ZKSYNC_TESTNET,
  PRICE_USDC_ID_ZKSYNC_MAINNET,
  PRICE_USDC_ID_ZKSYNC_TESTNET,
  TESTNET_ETH_PAYMASTER_ADDRESS,
  TESTNET_USDC_TOKEN_ADDRESS
} from '@constants/contracts'
import { PYTH_NETWORK_HERMES_BASE_URI_DEVELOPMENT, PYTH_NETWORK_HERMES_BASE_URI_PRODUCTION } from '@constants/oracles'
import { PaymasterTransactionType, ONE_TOKEN_18_DECIMALS, PaymasterFlow } from '@constants/paymaster'
import { useUserHasLowBalance } from '@hooks'
import { useEthersProvider } from '@hooks/useEthersProvider'
import { useEthersSigner } from '@hooks/useEthersSigner'

// TODO: We can delete it because we use Chainlink now
function getPythNetworkConfig(isMainnet: boolean): {
  hermesBaseURI: string
  ethPriceId: string
  usdcPriceId: string
} {
  if (isMainnet) {
    return {
      hermesBaseURI: PYTH_NETWORK_HERMES_BASE_URI_PRODUCTION,
      ethPriceId: PRICE_ETH_ID_ZKSYNC_MAINNET,
      usdcPriceId: PRICE_USDC_ID_ZKSYNC_MAINNET
    }
  } else {
    return {
      hermesBaseURI: PYTH_NETWORK_HERMES_BASE_URI_DEVELOPMENT,
      ethPriceId: PRICE_ETH_ID_ZKSYNC_TESTNET,
      usdcPriceId: PRICE_USDC_ID_ZKSYNC_TESTNET
    }
  }
}

export interface PaymasterOverrides {
  maxFeePerGas?: ethers.BigNumber
  maxPriorityFeePerGas?: ethers.BigNumber
  gasLimit?: ethers.BigNumber
  customData: {
    gasPerPubdata: number
    paymasterParams: PaymasterParams
  }
}

interface UsePaymasterOverridesProps {
  functionName: string
  args: any[]
  contract: string
  abi: any
  isMainnet: boolean
  paymasterTransactionType?: PaymasterTransactionType
  isMobileDevice: boolean
}

export const usePaymasterOverrides = ({
  functionName,
  args,
  contract,
  abi,
  isMainnet,
  paymasterTransactionType = PaymasterTransactionType.OracleUSDC,
  isMobileDevice
}: UsePaymasterOverridesProps) => {
  const [overrides, setOverrides] = useState<PaymasterOverrides | null>(null)
  const [userAddress, setUserAddress] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [zkSyncContract, setZkSyncContract] = useState<zkSyncWeb3.Contract | null>(null)
  const provider = useEthersProvider()
  const signer = useEthersSigner()
  const { userBalance } = useUserHasLowBalance({
    contract: isMainnet ? MAINNET_USDC_TOKEN_ADDRESS : TESTNET_USDC_TOKEN_ADDRESS,
    showToast: false,
    enabled: true
  })

  useEffect(() => {
    const fetchPaymasterParams = async () => {
      setLoading(true)
      setError(null)
      try {
        if (!signer) {
          throw new Error('No wallet client or signer')
        }

        if (!provider) {
          throw new Error('No provider found')
        }

        const zkSyncProvider = new zkSyncWeb3.Web3Provider(provider)
        const gasPrice = await zkSyncProvider.getGasPrice()

        const price = ethers.utils.formatEther(gasPrice.toString())
        const gasPriceParsed = ethers.utils.parseEther(price)

        const zkSyncSigner = zkSyncProvider.getSigner()
        const userAddress = await zkSyncSigner.getAddress()

        const openMintInstance = new zkSyncWeb3.Contract(contract, abi, zkSyncSigner)

        if (!openMintInstance) {
          console.error('Contract not found')
        }

        let paymasterOverrides
        switch (paymasterTransactionType) {
          case PaymasterTransactionType.OracleUSDC: {
            const paymasterAddress = isMainnet ? TENANT_CONTRACTS.mainnet.paymaster : TENANT_CONTRACTS.testnet.paymaster
            const tokenAddress = isMainnet ? MAINNET_USDC_TOKEN_ADDRESS : TESTNET_USDC_TOKEN_ADDRESS

            // @ts-ignore
            const gasLimit = await openMintInstance.estimateGas?.[functionName](...args, {
              customData: {
                gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT,
                paymasterParams: zkSyncWeb3.utils.getPaymasterParams(paymasterAddress, {
                  type: 'ApprovalBased',
                  token: tokenAddress,
                  minimalAllowance: ONE_TOKEN_18_DECIMALS,
                  innerInput: new Uint8Array()
                })
              }
            })
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const fee = gasPrice.mul(gasLimit)

            paymasterOverrides = {
              customData: {
                paymasterParams: zkSyncWeb3.utils.getPaymasterParams(paymasterAddress, {
                  type: PaymasterFlow.ApprovalBased,
                  token: tokenAddress,
                  minimalAllowance: ONE_TOKEN_18_DECIMALS,
                  innerInput: new Uint8Array()
                }),
                gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT
              }
            }

            break
          }
          case PaymasterTransactionType.PythOracleUsdc: {
            const paymasterAddress = isMainnet ? TENANT_CONTRACTS.mainnet.paymaster : TENANT_CONTRACTS.testnet.paymaster
            const tokenAddress = isMainnet ? MAINNET_USDC_TOKEN_ADDRESS : TESTNET_USDC_TOKEN_ADDRESS

            // @ts-ignore
            const gasLimit = await openMintInstance.estimateGas?.[functionName](...args, {
              customData: {
                gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT,
                paymasterParams: zkSyncWeb3.utils.getPaymasterParams(paymasterAddress, {
                  type: 'ApprovalBased',
                  token: tokenAddress,
                  minimalAllowance: ONE_TOKEN_18_DECIMALS,
                  innerInput: new Uint8Array()
                })
              }
            })
            const fee = gasPrice.mul(gasLimit)

            const pythNetworkConfig = getPythNetworkConfig(isMainnet)

            // ORACLE
            // Can change in the future
            const { data: hermesResponse } = await axios.get(
              `${pythNetworkConfig.hermesBaseURI}/api/latest_price_feeds?ids[]=${pythNetworkConfig.usdcPriceId}&ids[]=${pythNetworkConfig.ethPriceId}`
            )

            const usdcPriceString = hermesResponse[0].price.price
            const ethPriceString = hermesResponse[1].price.price

            const usdcExpo = hermesResponse[0].price.expo
            const ethExpo = hermesResponse[1].price.expo

            const USDCUSD = BigInt(usdcPriceString) * BigInt(10) ** BigInt(-usdcExpo)
            const ETHUSD = BigInt(ethPriceString) * BigInt(10) ** BigInt(-ethExpo)

            const usdFee = fee.mul(ETHUSD).div(USDCUSD)

            paymasterOverrides = {
              customData: {
                paymasterParams: zkSyncWeb3.utils.getPaymasterParams(paymasterAddress, {
                  type: PaymasterFlow.ApprovalBased,
                  token: tokenAddress,
                  minimalAllowance: usdFee,
                  innerInput: new Uint8Array()
                }),
                gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT
              }
            }
            break
          }

          case PaymasterTransactionType.USDCFixed: {
            const paymasterAddress = isMainnet ? TENANT_CONTRACTS.mainnet.paymaster : TENANT_CONTRACTS.testnet.paymaster
            const tokenAddress = isMainnet ? MAINNET_USDC_TOKEN_ADDRESS : TESTNET_USDC_TOKEN_ADDRESS

            const paymasterParams = zkSyncWeb3.utils.getPaymasterParams(paymasterAddress, {
              type: PaymasterFlow.ApprovalBased,
              token: tokenAddress,
              minimalAllowance: ONE_TOKEN_18_DECIMALS,
              innerInput: new Uint8Array()
            })

            paymasterOverrides = {
              maxFeePerGas: gasPriceParsed,
              maxPriorityFeePerGas: ethers.BigNumber.from(0),
              customData: {
                gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT,
                paymasterParams
              }
            }
            break
          }

          case PaymasterTransactionType.Gasless:
          default:
            {
              const paymasterAddress = isMainnet ? MAINNET_ETH_PAYMASTER_ADDRESS : TESTNET_ETH_PAYMASTER_ADDRESS

              const paymasterParams = zkSyncWeb3.utils.getPaymasterParams(paymasterAddress, {
                type: PaymasterFlow.General,
                innerInput: new Uint8Array()
              })

              const gasLimit = await openMintInstance.estimateGas?.[functionName](...args, {
                customData: {
                  gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT,
                  paymasterParams: paymasterParams
                }
              })

              paymasterOverrides = {
                maxFeePerGas: gasPriceParsed,
                maxPriorityFeePerGas: ethers.BigNumber.from(0),
                gasLimit,
                customData: {
                  gasPerPubdata: zkSyncWeb3.utils.DEFAULT_GAS_PER_PUBDATA_LIMIT,
                  paymasterParams
                }
              }
            }
            break
        }

        setOverrides(paymasterOverrides)
        setUserAddress(userAddress)
        setZkSyncContract(openMintInstance)
      } catch (error: any) {
        setError('Paymaster failed, try again later')
        console.error('Error with Paymaster: ', error)
      } finally {
        setLoading(false)
      }
    }

    const notValidArgs = args.some((arg) => arg == null)
    // TODO: check if the paymaster params works on mobile app wallets, but in the open-mint was broken
    if (
      functionName &&
      args?.length &&
      !notValidArgs &&
      provider &&
      !isMobileDevice &&
      !error &&
      !zkSyncContract &&
      userBalance > 0
    ) {
      fetchPaymasterParams()
    }
  }, [functionName, args, provider, signer, isMobileDevice, isMainnet])

  return { overrides, loading, error, userAddress, zkSyncContract }
}
